"use client"

import React, { useState, useRef } from "react";
import LocalizedClientLink from "@modules/common/components/localized-client-link";
import { ChevronDown } from "lucide-react";
import { HttpTypes } from "@medusajs/types"

interface Category extends Omit<HttpTypes.StoreProductCategory, "category_children"> {
  category_children?: Category[]
}

// Utility function similar to clsx/cn
const cn = (...classes: (string | boolean | undefined | null)[]) => {
  return classes.filter(Boolean).join(" ");
};

const capitalize = (str: string) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const CategoryItem = ({
  category,
  depth = 0,
}: {
  category: Category;
  depth?: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasChildren = category.category_children && category.category_children.length > 0;

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  return (
    <li className="relative">
      {hasChildren ? (
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="flex items-center"
        >
          <LocalizedClientLink
            href={`/categories/${category.handle}`}
            className={cn(
              "flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-red-500 transition-colors",
              isOpen && "text-red-500"
            )}
          >
            {capitalize(category.name)}
          </LocalizedClientLink>
          <button
            onClick={(e) => {
              e.preventDefault();
              setIsOpen(!isOpen);
            }}
            onMouseEnter={handleMouseEnter}
            className={cn(
              "p-1 text-gray-400 hover:text-red-500 transition-colors",
              isOpen && "text-red-500"
            )}
            aria-expanded={isOpen}
            aria-haspopup="menu"
            aria-label={
              isOpen
                ? `Cerrar subcategorías de ${capitalize(category.name)}`
                : `Abrir subcategorías de ${capitalize(category.name)}`
            }
          >
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform",
                isOpen && "rotate-180"
              )}
            />
          </button>
        </div>
      ) : (
        <LocalizedClientLink
          href={`/categories/${category.handle}`}
          className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-red-500 transition-colors"
        >
          {capitalize(category.name)}
        </LocalizedClientLink>
      )}

      {hasChildren && isOpen && (
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={cn(
            "absolute left-0 top-full min-w-[200px] bg-white shadow-lg rounded-lg border z-50",
            depth === 0 ? "mt-1" : "mt-0 ml-1"
          )}
        >
          <ul className="py-2">
            {category.category_children!.map((child) => (
              <CategoryItem key={child.id} category={child} depth={depth + 1} />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

const CategoriasNav = ({
  product_categories,
}: {
  product_categories: HttpTypes.StoreProductCategory[]
}) => {
  const topLevelCategories = product_categories.filter(
    (cat) => !cat.parent_category_id
  );

  // Debug: mostrar categorias en desarrollo
  if (process.env.NODE_ENV === "development") {
    // DEBUG: console.log("Total categorias:", product_categories.length);
    // DEBUG: console.log("Categorias nivel superior:", topLevelCategories.length);
  }

  return (
    <div className="w-full bg-white border-b border-ui-border-base">
      {/* Desktop Navbar */}
      <div className="hidden lg:block">
        <nav className="py-4" aria-label="Categorias">
          <ul className="flex justify-center items-center gap-2 font-sm font-medium text-gray-600">
            <li>
              <LocalizedClientLink
                href="/"
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-red-500 transition-colors"
              >
                Inicio
              </LocalizedClientLink>
            </li>

            {topLevelCategories.map((category) => (
              <CategoryItem
                key={category.id}
                category={category as Category}
              />
            ))}

            <li>
              <LocalizedClientLink
                href="/store"
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-red-500 transition-colors"
              >
                Tienda
              </LocalizedClientLink>
            </li>
          </ul>
        </nav>
      </div>

      {/* Mobile Navbar - Solo el boton de busqueda */}
      <div className="lg:hidden">
        <nav className="py-4 flex justify-center">
          {/* El menu mobile se maneja en SideMenu */}
        </nav>
      </div>
    </div>
  );
};

export default CategoriasNav;
